import { useCallback, useEffect, useId, useState } from 'react';

import { supabase } from '../auth/supabase-client';
import type { Notification } from '../data/types';
import { chargerNotifications, compterNonLues, marquerLue, toutMarquerLu } from './donnees';

type Etat = {
  liste: Notification[];
  nonLues: number;
  chargement: boolean;
  erreur: string | null;
};

const VIDE: Etat = { liste: [], nonLues: 0, chargement: true, erreur: null };

/**
 * La cloche : les dernières notifications de la personne connectée, tenues à
 * jour en temps réel. La liste s'arrête à trente lignes, la pastille non : elle
 * vient d'un comptage en base, sinon elle plafonnerait à trente et n'afficherait
 * jamais le « 99+ » prévu.
 */
export function useNotifications(monMembreId: string | null) {
  const [etat, setEtat] = useState<Etat>(VIDE);
  // La cloche est montée deux fois (panneau latéral et barre du haut). Deux
  // canaux de même nom, c'est le même canal côté client : le second
  // `on(...)` arriverait après un `subscribe()` et lèverait une erreur.
  const canalId = useId();

  const recharger = useCallback(async () => {
    if (!monMembreId) {
      setEtat({ ...VIDE, chargement: false });
      return;
    }
    try {
      const [liste, nonLues] = await Promise.all([
        chargerNotifications(monMembreId),
        compterNonLues(monMembreId),
      ]);
      setEtat({ liste, nonLues, chargement: false, erreur: null });
    } catch (cause) {
      setEtat((courant) => ({ ...courant, chargement: false, erreur: (cause as Error).message }));
    }
  }, [monMembreId]);

  useEffect(() => {
    void recharger();
  }, [recharger]);

  useEffect(() => {
    const client = supabase;
    if (!monMembreId || !client) return;
    const canal = client
      .channel(`cloche:${monMembreId}:${canalId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'alba',
          table: 'notifications',
          filter: `member_id=eq.${monMembreId}`,
        },
        () => {
          void recharger();
        },
      )
      .subscribe();
    return () => {
      void client.removeChannel(canal);
    };
  }, [canalId, monMembreId, recharger]);

  const lire = useCallback(async (id: string) => {
    setEtat((courant) => {
      const etaitNonLue = courant.liste.some((ligne) => ligne.id === id && ligne.read_at === null);
      const liste = courant.liste.map((ligne) =>
        ligne.id === id && ligne.read_at === null
          ? { ...ligne, read_at: new Date().toISOString() }
          : ligne,
      );
      return {
        ...courant,
        liste,
        nonLues: etaitNonLue ? Math.max(0, courant.nonLues - 1) : courant.nonLues,
      };
    });
    try {
      await marquerLue(id);
    } catch {
      // Le temps réel remettra la ligne dans son vrai état.
    }
  }, []);

  const toutLire = useCallback(async () => {
    if (!monMembreId) return;
    const maintenant = new Date().toISOString();
    setEtat((courant) => ({
      ...courant,
      liste: courant.liste.map((ligne) => ({ ...ligne, read_at: ligne.read_at ?? maintenant })),
      nonLues: 0,
    }));
    try {
      await toutMarquerLu(monMembreId);
    } catch {
      // Idem : la vérité revient par le temps réel.
    }
  }, [monMembreId]);

  return { ...etat, recharger, lire, toutLire };
}
